import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import HeroNeuralScene from './three/HeroNeuralScene';
import { ClaudeLogo, OpenAILogo, CopilotLogo, GeminiLogo } from './AIToolLogos';

const Hero: React.FC = () => {
  const [displayedText, setDisplayedText] = useState('');
  const fullText = 'AI-Native Engineering';

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setDisplayedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 80);

    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId.toLowerCase());
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="hero-section ai-hero" style={{ position: 'relative' }}>
      <HeroNeuralScene />
      <div className="hero-container" style={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          className="hero-badge"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <span className="badge-dot"></span>
          <span>AI-First Development</span>
        </motion.div>

        <motion.h1
          className="hero-title ai-title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="typewriter-text">{displayedText}<span className="cursor-blink">_</span></span>
          <br />
          <span className="hero-subtitle-inline">Intelligent Solutions</span>
        </motion.h1>

        <motion.p
          className="hero-description ai-description"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          We don't just use AI tools—we architect <strong>AI-first systems</strong> that think, learn, and scale.
          <br />
          Powered by Claude, GPT-4, and custom ML models.
        </motion.p>

        <motion.div
          className="hero-cta-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <motion.button
            onClick={() => scrollToSection('contact')}
            className="hero-primary-btn ai-cta"
            whileHover={{ scale: 1.05, boxShadow: '0 20px 60px -10px rgba(0, 212, 255, 0.6)' }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="btn-text">Start Your AI Project</span>
            <span className="btn-arrow">→</span>
          </motion.button>
        </motion.div>

        <motion.div
          className="hero-ai-powered"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
        >
          <p className="ai-tools-text">Powered by</p>
          <div className="ai-tools-grid">
            <motion.div
              className="ai-tool-badge"
              whileHover={{ y: -4, boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)' }}
            >
              <ClaudeLogo size={24} />
              <span>Claude</span>
            </motion.div>
            <motion.div
              className="ai-tool-badge"
              whileHover={{ y: -4, boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)' }}
            >
              <OpenAILogo size={24} />
              <span>GPT-4</span>
            </motion.div>
            <motion.div
              className="ai-tool-badge"
              whileHover={{ y: -4, boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)' }}
            >
              <CopilotLogo size={24} />
              <span>Copilot</span>
            </motion.div>
            <motion.div
              className="ai-tool-badge"
              whileHover={{ y: -4, boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)' }}
            >
              <GeminiLogo size={24} />
              <span>Gemini</span>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
